import { Icon } from '@cellula/react';
import { ErrorBoundary } from '@component/error_boundary.component';
import { useResolve } from '@visiba/instantiation';
import { ConcurrentPresentation, Presentation, usePresentation } from '@visiba/presentation';
import { sanitizedInjectHTML } from '@visiba/react-std';
import { rem } from '@visiba/std';
import { motion } from 'framer-motion';
import { Suspense, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { SvgHttpService } from './svg_http.service';

interface Props {
	imgSrc: string;
	size: number;
	overrideColor?: string;
}

interface PresentationModel {
	unsafeSvgString: string;
}

const elements = {
	container: styled.div<{ $size: number; $overrideColor?: string }>`
		display: flex;
		justify-content: center;
		align-items: center;
		width: ${({ $size }) => rem($size)};
		height: ${({ $size }) => rem($size)};
		font-size: ${({ $size }) => rem($size)};

		${({ $overrideColor }) => {
			if (!$overrideColor) return null;

			return `
				color: ${$overrideColor};
			`;
		}}

		& svg {
			width: 1em;
			height: 1em;
			display: inline-block;
			vertical-align: text-top;
			pointer-events: none;
			fill: currentColor;
		}
	`,
	svgContent: styled(motion.div)`
		display: flex;
		width: 100%;
		height: 100%;
	`,
};

const REGEX_TO_REMOVE_RANDOM_COLOR_MODIFIERS_IN_A_SVG = /(fill|class|style)="(.*?)"/g;

function Inner({ presentation }: { presentation: ConcurrentPresentation<PresentationModel> }): JSX.Element {
	const { unsafeSvgString } = usePresentation(presentation);
	const parsedUnsafeSvgString = unsafeSvgString.replace(REGEX_TO_REMOVE_RANDOM_COLOR_MODIFIERS_IN_A_SVG, '');

	if (parsedUnsafeSvgString === '') {
		return (
			<elements.svgContent data-testid='external_svg_inner' animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
				{/* TODO: this should be changed to some defaultish icon, we don't have that yet. [X] like this or something */}
				<Icon type='arrowLeft' data-testid='external_svg_error' />
			</elements.svgContent>
		);
	}

	return (
		<elements.svgContent
			data-testid='external_svg_inner'
			animate={{ opacity: 1 }}
			initial={{ opacity: 0 }}
			dangerouslySetInnerHTML={sanitizedInjectHTML(parsedUnsafeSvgString)}
		/>
	);
}

export function ExternalSvg({ imgSrc, size, overrideColor }: Props): JSX.Element {
	const svgHttpService = useResolve(SvgHttpService);
	const presentation = useMemo(() => Presentation.createConcurrent<PresentationModel>(), []);

	useEffect(() => {
		presentation
			.suspend(
				svgHttpService.get(imgSrc).then((response) => {
					if (response.ok) return response.ok.data;

					return '';
				}),
				(data) => {
					return {
						unsafeSvgString: data,
					};
				},
			)
			.gracefulDegradation(async () => {
				// TODO: update the Presentation api in some sense.
				// This is typed to return T but it's still being ran through the map
				// function, breaking things.
				return {
					unsafeSvgString: '',
				};
			});
	}, [imgSrc, presentation, svgHttpService]);

	return (
		<elements.container $size={size} $overrideColor={overrideColor}>
			<ErrorBoundary fallback={<elements.svgContent />}>
				<Suspense fallback={<elements.svgContent />}>
					<Inner presentation={presentation} />
				</Suspense>
			</ErrorBoundary>
		</elements.container>
	);
}
